import { GTM_ID } from "@const/shared"
import { useGoogleOneTapLogin } from "@hooks/google/useGoogleOneTapLogin"
import { ComponentProvider } from "@lib/context/ComponentContext"
import { loadScript, onFirstInteraction } from "@lib/functions/third-party"
import "dayjs/locale/id"
import { Inter, Urbanist } from "next/font/google"
import { Router } from "next/router"
import nProgress from "nprogress"
import "nprogress/nprogress.css"
import { useEffect } from "react"
import "react-multi-carousel/lib/styles.css"
import "styles/index.scss"
import Error from "./_error"

// `optional` + no preload: fonts never reflow text after first paint (the swap caused CLS up to
// 0.35) and their ~77 KB stay off the LCP critical path; cached visits render the web fonts.
const inter = Inter({ subsets: ["latin"], display: "optional", preload: false })
const urbanist = Urbanist({ subsets: ["latin"], display: "optional", preload: false })

// Cloudflare Web Analytics site tokens (public; same values Cloudflare used to auto-inject).
const CF_ANALYTICS_TOKENS: Record<string, string> = {
  "www.harisenin.com": "accecea6a67e48b7b39c5202bdea1370",
  "www.harisenin.net": "bc757aab33554e1ca6e99412ed0b149d",
}

/** Adds the analytics beacon after `load`, instead of Cloudflare's edge-injected high-priority copy. */
function loadCloudflareAnalytics() {
  const token = CF_ANALYTICS_TOKENS[window.location.hostname]
  // Edge auto-injection may still be on; never send a second beacon.
  if (!token || document.querySelector('script[src*="cloudflareinsights.com"]')) return

  const script = document.createElement("script")
  script.defer = true
  script.src = "https://static.cloudflareinsights.com/beacon.min.js"
  script.dataset.cfBeacon = JSON.stringify({ token, spa: true })
  document.body.appendChild(script)
}

const App: ({ Component, pageProps }: { Component: any; pageProps: any }) => React.JSX.Element = ({
  Component,
  pageProps,
}) => {
  Router.events.on("routeChangeStart", () => {
    nProgress.start()
  })
  Router.events.on("routeChangeComplete", () => {
    nProgress.done()
    // smoothScroll();
  })
  Router.events.on("routeChangeError", () => {
    nProgress.done()
  })

  useGoogleOneTapLogin()

  useEffect(
    () =>
      onFirstInteraction(() => {
        // Marquees and skeleton shimmers start moving here (see styles/_animate.scss).
        document.documentElement.classList.add("is-interacted")
        const w = window as typeof window & { dataLayer?: unknown[] }
        w.dataLayer = w.dataLayer || []
        w.dataLayer.push({ "gtm.start": new Date().getTime(), event: "gtm.js" })
        loadScript(`https://www.googletagmanager.com/gtm.js?id=${GTM_ID}`)
      }),
    []
  )

  useEffect(() => {
    if (document.readyState === "complete") {
      loadCloudflareAnalytics()
      return
    }
    window.addEventListener("load", loadCloudflareAnalytics, { once: true })
    return () => window.removeEventListener("load", loadCloudflareAnalytics)
  }, [])

  const fontVariables = (
    <style jsx global>{`
      :root {
        --font-inter: ${inter.style.fontFamily};
        --font-urbanist: ${urbanist.style.fontFamily};
      }
    `}</style>
  )

  if (pageProps.error) {
    return (
      <>
        {fontVariables}
        <Error />
      </>
    )
  }

  return (
    <>
      {fontVariables}
      <ComponentProvider>
        <Component {...pageProps} />
      </ComponentProvider>
    </>
  )
}
export default App
